import GraphQL from '@app/model/_common/service/GraphQL';
import { gql } from 'graphql-request';

const ROW_FIELDS = `
	key
	kind
	title
	situations { daysBefore roomNights occupiedRooms roomRevenue roomRevenueNet totalRevenue totalRevenueNet }
`;

const GROUPS_FIELDS = `
	groups { id title segments }
	segments { name properties roomNights }
	canEdit
`;

export const FIND_BI_SCOPE_QUERY: GraphQL = {
	name: 'findBiScope',
	gql: gql`
		query findBiScope {
			findBiScope {
				companyId
				companyName
				asOf
				companyProperties
				hotels { companyHotelId reservationsSource importedAt firstStay lastStay heldBlocks }
			}
		}
	`,
};

export const FIND_SEGMENTATION_REPORT_QUERY: GraphQL = {
	name: 'findSegmentationReport',
	gql: gql`
		query findSegmentationReport($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!, $view: SegmentationView!, $includeOptions: Boolean!) {
			findSegmentationReport(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo, view: $view, includeOptions: $includeOptions) {
				view
				stayFrom
				stayTo
				rows { ${ROW_FIELDS} segments { ${ROW_FIELDS} } }
				total { ${ROW_FIELDS} }
				reportRules { outOfOrderReducesRooms dayUseOccupied noShowOccupied houseUseOccupied roomRevenue }
				rulesDiffer
			}
		}
	`,
};

export const FIND_SEGMENT_EVOLUTION_QUERY: GraphQL = {
	name: 'findSegmentEvolution',
	gql: gql`
		query findSegmentEvolution($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!, $grain: EvolutionGrain!, $view: SegmentationView!, $includeOptions: Boolean!) {
			findSegmentEvolution(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo, grain: $grain, view: $view, includeOptions: $includeOptions) {
				stayFrom
				stayTo
				grain
				view
				periods { key label from to hasData }
				series { key kind title roomNights occupiedRooms roomRevenue roomRevenueNet totalRevenue totalRevenueNet }
				reportRules { outOfOrderReducesRooms dayUseOccupied noShowOccupied houseUseOccupied roomRevenue }
				rulesDiffer
			}
		}
	`,
};

export const FIND_SEGMENT_GROUPS_QUERY: GraphQL = {
	name: 'findSegmentGroups',
	gql: gql`
		query findSegmentGroups {
			findSegmentGroups { ${GROUPS_FIELDS} }
		}
	`,
};

export const SAVE_SEGMENT_GROUPS_MUTATION: GraphQL = {
	name: 'saveSegmentGroups',
	gql: gql`
		mutation saveSegmentGroups($groups: [SegmentGroupInput!]!) {
			saveSegmentGroups(groups: $groups) { ${GROUPS_FIELDS} }
		}
	`,
};

// --- Room Types report (BI report 2, ROO2-262) -----------------------------------------------------------------------
const FIGURES = 'roomNights roomRevenue roomRevenueNet totalRevenue totalRevenueNet';
// ROO2-315 part c1, c2: the figures of Room Types, Rate plans, Guest Mix and Channels with the occupied rooms of the report rules.
const ROOM_TYPE_FIGURES = `${FIGURES} occupiedRooms`;
const REPORT_RULES = 'reportRules { outOfOrderReducesRooms dayUseOccupied noShowOccupied houseUseOccupied roomRevenue } rulesDiffer';
const ROOM_TYPE_ROW_FIELDS = `
	key
	kind
	title
	rooms
	roomDays
	properties
	reservations
	situations { daysBefore ${ROOM_TYPE_FIGURES} }
	stly { ${ROOM_TYPE_FIGURES} }
	lyActual { ${ROOM_TYPE_FIGURES} }
	comparable { ${ROOM_TYPE_FIGURES} }
	assignedRoomNights
	assignedOccupiedRooms
	outOfOrderRooms
	months { month ${ROOM_TYPE_FIGURES} assignedRoomNights assignedOccupiedRooms roomDays outOfOrderRooms }
	channels { name reservations ${ROOM_TYPE_FIGURES} }
	boards { name reservations ${ROOM_TYPE_FIGURES} }
`;

export const FIND_ROOM_TYPE_REPORT_QUERY: GraphQL = {
	name: 'findRoomTypeReport',
	gql: gql`
		query findRoomTypeReport($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!, $basis: RoomTypeBasis!, $includeOptions: Boolean!) {
			findRoomTypeReport(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo, basis: $basis, includeOptions: $includeOptions) {
				basis
				stayFrom
				stayTo
				rows { ${ROOM_TYPE_ROW_FIELDS} }
				total { ${ROOM_TYPE_ROW_FIELDS} }
				properties { companyHotelId rows { ${ROOM_TYPE_ROW_FIELDS} } total { ${ROOM_TYPE_ROW_FIELDS} } }
				lastYearMissing
				${REPORT_RULES}
			}
		}
	`,
};

// --- Rate Plans report (BI report 3, ROO2-263) ------------------------------------------------------------------------
export const FIND_RATE_PLAN_REPORT_QUERY: GraphQL = {
	name: 'findRatePlanReport',
	gql: gql`
		query findRatePlanReport($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!, $includeOptions: Boolean!) {
			findRatePlanReport(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo, includeOptions: $includeOptions) {
				stayFrom
				stayTo
				parts {
					key
					title
					policy
					board
					companyHotelId
					situations { daysBefore ${ROOM_TYPE_FIGURES} }
					stly { ${ROOM_TYPE_FIGURES} }
					lyActual { ${ROOM_TYPE_FIGURES} }
				}
				lastYearMissing
				${REPORT_RULES}
			}
		}
	`,
};

// The build-up of the Rate Plans report (ROO2-267): RN and the revenue of the switches only (T6).
export const FIND_RATE_PLAN_EVOLUTION_QUERY: GraphQL = {
	name: 'findRatePlanEvolution',
	gql: gql`
		query findRatePlanEvolution($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!,
			$windowDays: Int!, $revenueScope: RevenueScope!, $vat: VatBasis!, $includeOptions: Boolean!) {
			findRatePlanEvolution(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo,
				windowDays: $windowDays, revenueScope: $revenueScope, vat: $vat, includeOptions: $includeOptions) {
				days
				series { key title policy board thisYearRn thisYearRev lastYearRn lastYearRev thisYearOccupied lastYearOccupied }
				${REPORT_RULES}
			}
		}
	`,
};

// --- Guest Mix report (BI report 4, ROO2-264) -------------------------------------------------------------------------
export const FIND_GUEST_MIX_REPORT_QUERY: GraphQL = {
	name: 'findGuestMixReport',
	gql: gql`
		query findGuestMixReport($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!, $split: GuestMixSplit!, $includeOptions: Boolean!) {
			findGuestMixReport(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo, split: $split, includeOptions: $includeOptions) {
				split
				stayFrom
				stayTo
				parts {
					key
					companyHotelId
					situations { daysBefore ${ROOM_TYPE_FIGURES} }
					stly { ${ROOM_TYPE_FIGURES} }
					lyActual { ${ROOM_TYPE_FIGURES} }
				}
				lastYearMissing
				${REPORT_RULES}
			}
		}
	`,
};

// --- Property groups (BI Property groups) ------------------------------------------------------------------------------

const PROPERTY_GROUPS_FIELDS = `
	groups { id title companyHotelIds subtotalOnAll }
	canEdit
`;

export const FIND_PROPERTY_GROUPS_QUERY: GraphQL = {
	name: 'findPropertyGroups',
	gql: gql`
		query findPropertyGroups {
			findPropertyGroups { ${PROPERTY_GROUPS_FIELDS} }
		}
	`,
};

// One group saves alone: id null creates it, an id updates its name and its properties.
export const SAVE_PROPERTY_GROUP_MUTATION: GraphQL = {
	name: 'savePropertyGroup',
	gql: gql`
		mutation savePropertyGroup($group: PropertyGroupInput!) {
			savePropertyGroup(group: $group) { ${PROPERTY_GROUPS_FIELDS} }
		}
	`,
};

export const DELETE_PROPERTY_GROUP_MUTATION: GraphQL = {
	name: 'deletePropertyGroup',
	gql: gql`
		mutation deletePropertyGroup($id: Long!) {
			deletePropertyGroup(id: $id) { ${PROPERTY_GROUPS_FIELDS} }
		}
	`,
};

// ROO2-265: how each key of the split built up over the window (30, 90, 180 or 365 days to today).
export const FIND_GUEST_MIX_EVOLUTION_QUERY: GraphQL = {
	name: 'findGuestMixEvolution',
	gql: gql`
		query findGuestMixEvolution($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!, $split: GuestMixSplit!, $windowDays: Int!, $includeOptions: Boolean!) {
			findGuestMixEvolution(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo, split: $split, windowDays: $windowDays, includeOptions: $includeOptions) {
				split
				days
				series {
					key
					thisYear { ${ROOM_TYPE_FIGURES} }
					lastYear { ${ROOM_TYPE_FIGURES} }
				}
				${REPORT_RULES}
			}
		}
	`,
};

// ROO2-283: the Columns choice of the user for a BI report (null: no choice yet); a save replaces the whole list.
export const FIND_BI_COLUMNS_QUERY: GraphQL = {
	name: 'findBiColumns',
	gql: gql`
		query findBiColumns($report: BiReport!) { findBiColumns(report: $report) { report columns { id visible } } }
	`,
};

export const SAVE_BI_COLUMNS_MUTATION: GraphQL = {
	name: 'saveBiColumns',
	gql: gql`
		mutation saveBiColumns($report: BiReport!, $columns: [BiColumnInput!]!) { saveBiColumns(report: $report, columns: $columns) { report columns { id visible } } }
	`,
};

// --- Channels report (BI report 5, ROO2-285) -----------------------------------------------------------------------------
export const FIND_CHANNEL_REPORT_QUERY: GraphQL = {
	name: 'findChannelReport',
	gql: gql`
		query findChannelReport($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!, $includeOptions: Boolean!) {
			findChannelReport(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo, includeOptions: $includeOptions) {
				stayFrom
				stayTo
				parts {
					name
					key
					companyHotelId
					situations { daysBefore ${ROOM_TYPE_FIGURES} }
					stly { ${ROOM_TYPE_FIGURES} }
					lyActual { ${ROOM_TYPE_FIGURES} }
				}
				lastYearMissing
				${REPORT_RULES}
			}
		}
	`,
};

// The build-up of the Channels report: RN and the revenue of the switches only, as the Rate Plans build-up.
export const FIND_CHANNEL_EVOLUTION_QUERY: GraphQL = {
	name: 'findChannelEvolution',
	gql: gql`
		query findChannelEvolution($companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!,
			$windowDays: Int!, $revenueScope: RevenueScope!, $vat: VatBasis!, $includeOptions: Boolean!) {
			findChannelEvolution(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom, stayTo: $stayTo,
				windowDays: $windowDays, revenueScope: $revenueScope, vat: $vat, includeOptions: $includeOptions) {
				days
				series { name key thisYearRn thisYearRev lastYearRn lastYearRev thisYearOccupied lastYearOccupied }
				${REPORT_RULES}
			}
		}
	`,
};

const CHANNEL_GROUPS_FIELDS = `
	groups { id title keys }
	channels { key title spellings properties roomNights revenue firstBooking }
	savedAt
	canEdit
`;

export const FIND_CHANNEL_GROUPS_QUERY: GraphQL = {
	name: 'findChannelGroups',
	gql: gql`
		query findChannelGroups {
			findChannelGroups { ${CHANNEL_GROUPS_FIELDS} }
		}
	`,
};

export const SAVE_CHANNEL_GROUPS_MUTATION: GraphQL = {
	name: 'saveChannelGroups',
	gql: gql`
		mutation saveChannelGroups($groups: [ChannelGroupInput!]!) {
			saveChannelGroups(groups: $groups) { ${CHANNEL_GROUPS_FIELDS} }
		}
	`,
};

// ROO2-288: the Daily Performance report of one property (the figures of each night at each cut).
export const FIND_DAILY_PERFORMANCE_QUERY: GraphQL = {
	name: 'findDailyPerformance',
	gql: gql`
		query findDailyPerformance($companyHotelId: Long!, $stayFrom: LocalDate!, $stayTo: LocalDate!, $stlyBasis: StlyBasis!, $includeOptions: Boolean!) {
			findDailyPerformance(companyHotelId: $companyHotelId, stayFrom: $stayFrom, stayTo: $stayTo, stlyBasis: $stlyBasis, includeOptions: $includeOptions) {
				companyHotelId stayFrom stayTo stlyBasis includeOptions asOf importedAt rooms roomTypesWithoutCount firstStayNight
				held { night blocked onOption }
				nights { night cut occupiedRooms heldBlockRooms ${FIGURES} }
				reportRules { outOfOrderReducesRooms dayUseOccupied noShowOccupied houseUseOccupied roomRevenue }
				available { night outOfOrder availableRooms lastYearAvailableRooms }
			}
		}
	`,
};

// ROO2-314 Part 3: the NEW nights of each PU block of the same window (New RN and New ADR).
export const FIND_DAILY_NEW_NIGHTS_QUERY: GraphQL = {
	name: 'findDailyNewNights',
	gql: gql`
		query findDailyNewNights($companyHotelId: Long!, $stayFrom: LocalDate!, $stayTo: LocalDate!, $includeOptions: Boolean!) {
			findDailyNewNights(companyHotelId: $companyHotelId, stayFrom: $stayFrom, stayTo: $stayTo, includeOptions: $includeOptions) {
				night cut ${FIGURES}
			}
		}
	`,
};

// ROO2-289: the compset of each night of the window (at most 6 months) of one property.
export const FIND_DAILY_COMPSET_QUERY: GraphQL = {
	name: 'findDailyCompset',
	gql: gql`
		query findDailyCompset($companyHotelId: Long!, $from: LocalDate!, $to: LocalDate!) {
			findDailyCompset(companyHotelId: $companyHotelId, from: $from, to: $to) {
				comparisonMethod weightedAverageAvailable nights currency
				days { date status ownPrice updatedAt median { price difference tone } weightedAverage { price difference tone } }
			}
		}
	`,
};

// --- Bookings behind a number (ROO2-284) -------------------------------------------------------------------------------
export const FIND_BI_BOOKINGS_QUERY: GraphQL = {
	name: 'findBiBookings',
	gql: gql`
		query findBiBookings($report: BiBookingReport!, $companyHotelId: Long, $propertyGroupId: Long, $stayFrom: LocalDate!, $stayTo: LocalDate!,
			$cut: BiBookingCut!, $keys: [String!]!, $name: String, $includeOptions: Boolean!) {
			findBiBookings(report: $report, companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, stayFrom: $stayFrom,
				stayTo: $stayTo, cut: $cut, keys: $keys, name: $name, includeOptions: $includeOptions) {
				cut
				stayFrom
				stayTo
				more
				rows {
					companyHotelId reservationCode reservationNumber otaNumber bookDate arrival departure nights rooms channelName status change ${FIGURES}
				}
			}
		}
	`,
};

// ROO2-314 Part 2 (B2, B3): the bookings of the period with their reference ADRs, loaded apart from the figures.
const PICKUP_REVENUES = 'roomRevenue roomRevenueNet totalRevenue totalRevenueNet';
export const FIND_PICKUP_BOOKINGS_QUERY: GraphQL = {
	name: 'findPickupBookings',
	gql: gql`
		query findPickupBookings($companyHotelId: Long, $propertyGroupId: Long, $period: PickupPeriod!, $bookFrom: LocalDate, $bookTo: LocalDate,
			$includeOptions: Boolean!) {
			findPickupBookings(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, period: $period, bookFrom: $bookFrom, bookTo: $bookTo,
				includeOptions: $includeOptions) {
				bookFrom bookTo asOf tooMany
				properties {
					companyHotelId
					bookings {
						id reservationNumber otaNumber bookedAt change roomNights rooms nights arrival departure channel roomNightsNow roomTypes
						revenue { ${PICKUP_REVENUES} } revenueNow { ${PICKUP_REVENUES} }
						refRoomType { ${PICKUP_REVENUES} } refHotelNights { ${PICKUP_REVENUES} } refArrivalMonth { ${PICKUP_REVENUES} }
					}
				}
			}
		}
	`,
};

// ROO2-314 D9: the ADR alert setting of the user (basis, direction, percent), one setting for all properties.
export const FIND_PICKUP_ALERT_SETTING_QUERY: GraphQL = {
	name: 'findPickupAlertSetting',
	gql: gql`query findPickupAlertSetting { findPickupAlertSetting { basis direction threshold } }`,
};

export const SAVE_PICKUP_ALERT_SETTING_MUTATION: GraphQL = {
	name: 'savePickupAlertSetting',
	gql: gql`
		mutation savePickupAlertSetting($basis: PickupAlertBasis!, $direction: PickupAlertDirection!, $threshold: Int!) {
			savePickupAlertSetting(basis: $basis, direction: $direction, threshold: $threshold) { basis direction threshold }
		}
	`,
};

// ROO2-314 Part 2: Pick-up monitoring of a scope and a booked period (the shared new-bookings rule of Part 1).
export const FIND_PICKUP_MONITORING_QUERY: GraphQL = {
	name: 'findPickupMonitoring',
	gql: gql`
		query findPickupMonitoring($companyHotelId: Long, $propertyGroupId: Long, $period: PickupPeriod!, $bookFrom: LocalDate, $bookTo: LocalDate,
			$includeOptions: Boolean!) {
			findPickupMonitoring(companyHotelId: $companyHotelId, propertyGroupId: $propertyGroupId, period: $period, bookFrom: $bookFrom, bookTo: $bookTo,
				includeOptions: $includeOptions) {
				bookFrom bookTo asOf
				properties {
					companyHotelId madeAndCancelled
					changes { change bookings }
					cells { stayMonth kind roomNights roomRevenue roomRevenueNet totalRevenue totalRevenueNet }
				}
			}
		}
	`,
};
